import React from "react";
import { DateTime } from "luxon";
import {
  useRetrieveGantt,
  useRetrieveGanttCalendarRange,
  useRetrievePreferences,
} from "@planzone/view-models";
import { useMessage } from "@components/useMessage";
import { Gantt } from "@scenes/optimizedGantt";

import { Props } from "./types";
import { GanttSort } from "@planzone/dto";

function GanttPage({ zone }: Props) {
  const { preferences } = useRetrievePreferences();
  const savedSort: GanttSort = (preferences[
    `${zone.uuid}:ganttSort` as keyof typeof preferences
  ] || "ascendingRank") as GanttSort;
  const [sort, setSort] = React.useState<GanttSort>(savedSort);
  const {
    isRequestPending,
    gantt,
    currentPage,
    canRetrieveNextElements,
    retrieveGantt,
  } = useRetrieveGantt(10, sort, zone);
  const {
    isRequestPending: isCalendarRequestPending,
    isRequestSuccess: isCalendarRequestSuccess,
    isRequestFailure: isCalendarRequestFailure,
    range,
    retrieveGanttCalendarRange,
  } = useRetrieveGanttCalendarRange(zone);

  React.useEffect(() => {
    !isCalendarRequestPending &&
      !isCalendarRequestSuccess &&
      !isCalendarRequestFailure &&
      retrieveGanttCalendarRange({ zone });
  }, []);

  useMessage(
    ({ type }) =>
      ["Task", "List", "Milestone", "Event"].find(
        (elementType) =>
          type === `create${elementType}Succeeded` ||
          type === `edit${elementType}Succeeded`,
      ) && retrieveGanttCalendarRange({ zone }),
  );

  return (
    <>
      <Gantt
        projects={gantt.projects}
        range={
          !!range[0] && !!range[1]
            ? { minDate: range[0], maxDate: range[1] }
            : (() => {
                const now = DateTime.now().toUTC().toISO() ?? "";
                return { minDate: now, maxDate: now };
              })()
        }
        filter={{
          scope: "zone",
          elementTypes: ["list", "task", "milestone", "event", "completedWork"],
        }}
        sort="ascendingRank"
        namespace={zone.uuid}
        isFetchingProjects={isRequestPending}
        canRetrieveProjects={!isRequestPending && canRetrieveNextElements}
        retrieveProjects={() =>
          retrieveGantt({ size: 10, page: currentPage, sort, zone })
        }
        selectSort={(nextSort) => sort !== nextSort && setSort(nextSort)}
      />
    </>
  );
}

export { GanttPage };
